/**
 * Telegram 消息 → 内控上报字段的**纯归一化**。
 *
 * 单独成文件是为了能在不拖进 tweb 整个依赖图的前提下单测：这是整条上报链路里
 * 唯一有分支的地方，而它跑在 AI 回复/翻译/粉丝同步共用的事件里，判错代价高。
 *
 * 字段语义与 WhatsApp 那条路一致（两边共用客户端渲染层的同一份处理逻辑），
 * 见 dmex 的 `modules/ws/src/chat-normalize.js` 与 `api/chat-report.ts`。
 */

/** 正文上限，与 WhatsApp 那条路保持一致 */
export const CONTENT_MAX = 8192

export const IMAGE_PLACEHOLDER = '[图片]'

/** Telegram */
export const SYS_TYPE_TELEGRAM = 1

/**
 * Telegram 官方通知账号：这些不是客户会话，上报没有审计价值。
 * 与 AI 回复那边用的是同一份名单（见 utils/pcApi.js 的 message_receive）。
 */
const OFFICIAL_IDS = new Set(['777000', '429000', '333000', '42777']);

export const MESSAGE_TYPE_TEXT = 1;
export const MESSAGE_TYPE_IMAGE = 2;

/** 上报用的单条载荷，字段与 dmex 的 RawChatMessage 对齐 */
export interface TelegramChatReportItem {
  idempotencyKey: string;
  itemIndex: number;
  peerSystemId: string;
  senderType: 1 | 2;
  externalMessageId: string;
  messageType: number;
  content: string;
  messageTime: number;
}

function truncate(text: string): string {
  return text.length > CONTENT_MAX ? text.slice(0, CONTENT_MAX) : text;
}

/**
 * 判断消息类型。
 *
 * 只认「纯文本」与「图片」：Telegram 的 `messageMediaPhoto` 是图片；
 * 文档类媒体（视频、语音、贴纸、文件）本期不上报 —— 它们的正文语义各不相同，
 * 需要单独定义，猜一个反而会污染审计数据。
 */
function lookupMessageType(message: any): number | null {
  if (message._ !== 'message') return null;

  const media = message.media;
  if (!media) return MESSAGE_TYPE_TEXT;
  if (media._ === 'messageMediaPhoto') return MESSAGE_TYPE_IMAGE;

  // messageMediaDocument（视频/语音/贴纸/文件）及其它一律跳过
  return null;
}

/**
 * 归一化一条 Telegram 消息。不满足上报条件返回 null。
 *
 * 全函数：任何输入都返回字段对象或 null，**不抛错**。
 */
export function normalizeTelegramMessage(message: any): TelegramChatReportItem | null {
  if (!message || typeof message !== 'object') return null;

  // 只上报 1:1 用户会话：内控关心的是客户对话。群/频道另有口径，本期不含
  if (message.peer_id?._ !== 'peerUser') return null;

  const messageType = lookupMessageType(message);
  if (messageType === null) return null;

  const peerId = message.peerId;
  const mid = message.mid;
  // 两者都必须有：peerId + mid 是幂等键的全部来源，缺一个就既无法去重也无法对账
  if (peerId === undefined || peerId === null) return null;
  if (mid === undefined || mid === null) return null;

  const peerSystemId = String(peerId);
  if (!peerSystemId || OFFICIAL_IDS.has(peerSystemId)) return null;

  // `message` 字段在 Telegram 里既是文本正文、也是媒体的配文，所以两种类型共用
  let content = truncate(typeof message.message === 'string' ? message.message : '').trim();
  if (!content) {
    // 图片必须上报：没有配文时用占位符。纯文本为空则没有审计价值，跳过
    if (messageType !== MESSAGE_TYPE_IMAGE) return null;
    content = IMAGE_PLACEHOLDER;
  }

  // `mid` 在 tweb 里是会话内唯一的合成 id，配上 peerId 就是全局稳定键。
  // 不单用 `message.id`（原始 Telegram id）：它在不同会话间会重复。
  return {
    idempotencyKey: `tg:${peerSystemId}:${mid}`,
    itemIndex: 0,
    peerSystemId,
    senderType: message.pFlags?.out === true ? 1 : 2,
    // 有原始 id 就带上（对账时能和 Telegram 侧对上），否则回落 mid
    externalMessageId: String(message.id ?? mid),
    messageType,
    content,
    // Telegram 的 date 是秒级；缺失或非正数时用当前时间，不要上报 0
    messageTime:
      typeof message.date === 'number' && message.date > 0 ? message.date * 1000 : Date.now()
  };
}
